import loadable from "@loadable/component"
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import { setExperiment } from "app-store-v2/app/actions"
import { setHeaderStickyContentOffset } from "app-store-v2/header/actions"
import { retry } from "helpers/application"
import useWindowDimensions from "helpers/window-dimensions"
import { cn } from "styles/utils"

import DesktopNav from "./desktop-nav"
import MobileHeader from "./mobile-header"

const CountdownBanner = loadable(() =>
  retry(() => import("areas/header/countdown-banner"))
)

const HeaderContent = () => {
  const dispatch = useDispatch()
  const microbanner = useSelector(
    (state) => state.sale.microbanner,
    shallowEqual
  )
  const secondMicroBanner = useSelector(
    (state) => state.sale.second_microbanner,
    shallowEqual
  )
  const collectionUrlPath = useSelector((state) => state.collection.urlPath)
  const countdownMicrobannerExp = useSelector(
    (state) => state.experiment.countdownMicrobanner
  )

  const [sticky, setSticky] = useState(false)
  const [hideStickyNav, setHideStickyNav] = useState(false)
  const [animateStickyNav, setAnimateStickyNav] = useState(false)
  const [headerMeasurements, setHeaderMeasurements] = useState({
    headerHeight: 0,
    primaryHeaderHeight: 0,
  })
  const headerRef = useRef()
  const rafRef = useRef()
  const lastScrollYRef = useRef(0)
  const hasScrolledPastHeaderRef = useRef(false)
  const isTickingRef = useRef(false)
  const headerHeightRef = useRef(0)
  const primaryHeaderRef = useRef()
  const primaryHeaderHeightRef = useRef(0)
  const location = useLocation()
  const locationRef = useRef(location)

  const { isMobile } = useWindowDimensions()

  const isPDP = location.pathname.includes("/product/")
  const isCollection =
    collectionUrlPath && collectionUrlPath === location.pathname

  useEffect(() => {
    if (countdownMicrobannerExp) {
      dispatch(setExperiment({ countdownMicrobanner: countdownMicrobannerExp }))
    }
  }, [countdownMicrobannerExp, dispatch])

  const shouldHideNavOnScrollDown = useMemo(
    () => isMobile && (isPDP || isCollection),
    [isMobile, isPDP, isCollection]
  )

  const shouldHideRef = useRef(shouldHideNavOnScrollDown)
  const shouldPinSecondMicroBannerRef = useRef(
    Boolean(shouldHideNavOnScrollDown && secondMicroBanner)
  )

  // Keep refs updated for the persistent scroll listener
  useEffect(() => {
    locationRef.current = location
    shouldHideRef.current = shouldHideNavOnScrollDown
    shouldPinSecondMicroBannerRef.current = Boolean(
      shouldHideNavOnScrollDown && secondMicroBanner
    )
  }, [location, secondMicroBanner, shouldHideNavOnScrollDown])

  const updateHeaderHeight = useCallback(() => {
    const nextHeaderHeight = headerRef.current?.offsetHeight || 0
    const nextPrimaryHeaderHeight = primaryHeaderRef.current?.offsetHeight || 0

    if (
      headerHeightRef.current !== nextHeaderHeight ||
      primaryHeaderHeightRef.current !== nextPrimaryHeaderHeight
    ) {
      headerHeightRef.current = nextHeaderHeight
      primaryHeaderHeightRef.current = nextPrimaryHeaderHeight
      setHeaderMeasurements({
        headerHeight: nextHeaderHeight,
        primaryHeaderHeight: nextPrimaryHeaderHeight,
      })
    }
  }, [])

  // Initial measurement and resize listener
  useEffect(() => {
    updateHeaderHeight()
    window.addEventListener("resize", updateHeaderHeight)
    return () => window.removeEventListener("resize", updateHeaderHeight)
  }, [updateHeaderHeight])

  useEffect(() => {
    updateHeaderHeight()
  }, [isMobile, updateHeaderHeight])

  useEffect(() => {
    const handleScroll = () => {
      if (!isTickingRef.current) {
        isTickingRef.current = true
        rafRef.current = window.requestAnimationFrame(() => {
          if (headerRef.current) {
            const currentScrollY = window.scrollY
            const headerHeight = headerHeightRef.current
            const primaryHeaderHeight = primaryHeaderHeightRef.current

            setSticky((prev) => {
              const stickyThreshold = shouldPinSecondMicroBannerRef.current
                ? prev
                  ? 0
                  : primaryHeaderHeight
                : headerHeight + 50
              const next =
                !locationRef.current.pathname.includes("cart") &&
                currentScrollY > stickyThreshold
              return prev === next ? prev : next
            })

            if (currentScrollY > headerHeight * 2) {
              hasScrolledPastHeaderRef.current = true
            } else if (currentScrollY === 0) {
              hasScrolledPastHeaderRef.current = false
            }

            setAnimateStickyNav((prev) => {
              const next = shouldHideRef.current
                ? currentScrollY > headerHeight * 2
                : true
              return prev === next ? prev : next
            })

            if (shouldHideRef.current) {
              const shouldRevealNearTop =
                hasScrolledPastHeaderRef.current &&
                currentScrollY < headerHeight
              setHideStickyNav((prev) => {
                const next =
                  shouldRevealNearTop ||
                  (currentScrollY < lastScrollYRef.current &&
                    (prev || currentScrollY > headerHeight * 2))
                return prev === next ? prev : next
              })
            }

            lastScrollYRef.current = currentScrollY
          }
          isTickingRef.current = false
        })
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.cancelAnimationFrame(rafRef.current)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const shouldPinSecondMicroBanner = Boolean(
    shouldHideNavOnScrollDown && secondMicroBanner
  )
  const stickyHeaderOffset =
    shouldPinSecondMicroBanner && sticky && !hideStickyNav
      ? -headerMeasurements.primaryHeaderHeight
      : 0
  let stickyContentOffset = 0
  if (shouldHideNavOnScrollDown && sticky) {
    stickyContentOffset = shouldPinSecondMicroBanner
      ? Math.max(headerMeasurements.headerHeight + stickyHeaderOffset, 0)
      : hideStickyNav
        ? headerMeasurements.headerHeight
        : 0
  }

  useLayoutEffect(() => {
    dispatch(setHeaderStickyContentOffset(stickyContentOffset))
  }, [dispatch, stickyContentOffset])

  return (
    <header
      className={cn(
        "backface-hidden border-b-grey-10 top-0 z-40 flex w-full flex-col bg-white",
        animateStickyNav
          ? "duration-400 transition-all ease-out"
          : "transition-shadow",
        shouldHideNavOnScrollDown
          ? sticky &&
              (shouldPinSecondMicroBanner
                ? "sticky"
                : hideStickyNav
                  ? "sticky translate-y-0"
                  : "-translate-y-full")
          : "sticky",
        sticky ? "shadow-lg" : "shadow-none",
        location.pathname.includes("cart") && "tablet:static"
      )}
      ref={headerRef}
      style={
        shouldPinSecondMicroBanner
          ? { transform: `translateY(${stickyHeaderOffset}px)` }
          : undefined
      }
    >
      <div ref={primaryHeaderRef}>
        <CountdownBanner microbanner={microbanner} />
        <MobileHeader />
        <DesktopNav />
      </div>
      {secondMicroBanner && (
        <CountdownBanner
          isSecondMicroBanner
          microbanner={secondMicroBanner}
          // showOnMobile={!shouldSimplifyAboveFoldOnMobile}
        />
      )}
    </header>
  )
}

export default memo(HeaderContent)
